/**
 * Created by Jerker Nord on 2023-09-25.
 */
import {Loner} from "../../../Loner";
import {LonerGameAssets} from "../../../LonerGameAssets";
import {gsap} from "gsap";
import {LonerSound} from "../../../sound/LonerSound";
import {TimelineAnimatedSprite} from "../../../utils/TimelineAnimatedSprite";

export class LonerCar extends PIXI.Container {

    private static CAR_EXHAUST_POSITION: PIXI.Point = new PIXI.Point(-20, -50);
    private static CAR_ENGINE_FIRE_POSITION: PIXI.Point = new PIXI.Point(108, 10);

    private _carSprite: PIXI.Sprite;
    private _explosionAnimation: PIXI.Sprite;
    private _carRunningTL: gsap.core.Timeline;
    private _normalExplosionTL: gsap.core.Timeline;
    private _maxWinExplosionTL: gsap.core.Timeline;
    private _breakDown01TL: gsap.core.Timeline;
    private _engineFireAnimation: TimelineAnimatedSprite;
    private _carExhaustAnimation: TimelineAnimatedSprite;
    private _carExhaustAnimationTL: gsap.core.Timeline;

    constructor() {
        super();
        this.init();
    }

    private init(): void {
        this._carSprite = new PIXI.Sprite(Loner.assetLoader.getTextures(LonerGameAssets.TTB_CAR_PIXELS)[0]);
        this._carSprite.scale.set(3, 3);
        this.addChild(this._carSprite);

        this._carRunningTL = gsap.timeline({paused: true, repeat: -1});
        this._carRunningTL.add(gsap.to(this._carSprite.position, {
            ease: "none",
            duration: 0.08,
            y: -1
        }));
        this._carRunningTL.add(gsap.to(this._carSprite.position, {
            ease: "none",
            duration: 0.08,
            y: 0
        }));

        this._engineFireAnimation = new TimelineAnimatedSprite(Loner.assetLoader.getTextures(LonerGameAssets.TTB_ENGINE_FIRE_ANIMATION), -1, 8);
        this._engineFireAnimation.anchor.set(0.5, 0.5);
        this._engineFireAnimation.position = LonerCar.CAR_ENGINE_FIRE_POSITION;
        this._engineFireAnimation.visible = false;
        this.addChild(this._engineFireAnimation);
        this._engineFireAnimation.play();

        this._explosionAnimation = new PIXI.Sprite(Loner.assetLoader.getTextures("TickTickBoom/explosion")[0]);
        this._explosionAnimation.anchor.set(0.5, 0.5);
        this._explosionAnimation.position.x = this._carSprite.width / 2 - 20;
        this._explosionAnimation.position.y = +20;
        this._explosionAnimation.scale.set(0.1, 0.1);
        this._explosionAnimation.visible = false;
        this.addChild(this._explosionAnimation);

        this._normalExplosionTL = gsap.timeline({paused: true});

        const rotateTL: gsap.core.Timeline = gsap.timeline();
        for (let i: number = 1; i < 4; i++) {
            rotateTL.add(gsap.to(this._explosionAnimation, {
                ease: "none",
                duration: 0.05,
                angle: 20 * i
            }));
            rotateTL.add(gsap.timeline({delay: 0.07}));
        }
        const scaleUpTL: gsap.core.Timeline = gsap.timeline();
        for (let i: number = 1; i < 5; i++) {
            scaleUpTL.add(gsap.to(this._explosionAnimation.scale, {
                ease: "none",
                duration: 0.05,
                x: 0.30 * i,
                y: 0.30 * i
            }));
            scaleUpTL.add(gsap.timeline({delay: 0.07}));
        }

        this._normalExplosionTL.add(() => {
            this._explosionAnimation.visible = true;
            Loner.soundPlayer.playSound(LonerSound.TTB_CAR_EXPLODE);
            Loner.soundPlayer.stopSound(LonerSound.TTB_CAR_DRIVING);
        });
        this._normalExplosionTL.add([
            rotateTL,
            scaleUpTL
        ]);
        this._normalExplosionTL.add(() => {
            this._carSprite.visible = false;
            this._engineFireAnimation.visible = false;
        });
        this._normalExplosionTL.add(gsap.timeline({delay: 0.3}));
        this._normalExplosionTL.add(() => {
            this._explosionAnimation.visible = false;
            this._explosionAnimation.scale.set(1, 1);
        });

        this._maxWinExplosionTL = gsap.timeline({paused: true});
        const rotateMaxWinTL: gsap.core.Timeline = gsap.timeline();
        for (let i: number = 1; i < 4; i++) {
            rotateMaxWinTL.add(gsap.to(this._explosionAnimation, {
                ease: "none",
                duration: 0.05,
                angle: 20 * i
            }));
            rotateMaxWinTL.add(gsap.timeline({delay: 0.07}));
        }
        const scaleUpMaxWinTL: gsap.core.Timeline = gsap.timeline();
        for (let i: number = 1; i < 5; i++) {
            scaleUpMaxWinTL.add(gsap.to(this._explosionAnimation.scale, {
                ease: "none",
                duration: 0.05,
                x: 0.8 * i,
                y: 0.8 * i
            }));
            scaleUpMaxWinTL.add(gsap.timeline({delay: 0.07}));
        }

        this._maxWinExplosionTL.add(() => {
            this._explosionAnimation.visible = true;
            Loner.soundPlayer.playSound(LonerSound.TTB_CAR_EXPLODE);
            Loner.soundPlayer.stopSound(LonerSound.TTB_CAR_DRIVING);
        });
        this._maxWinExplosionTL.add([
            rotateMaxWinTL,
            scaleUpMaxWinTL
        ]);
        this._maxWinExplosionTL.add(() => {
            this._carSprite.visible = false;
            this._engineFireAnimation.visible = false;
        });
        this._maxWinExplosionTL.add(gsap.timeline({delay: 0.3}));
        this._maxWinExplosionTL.add(() => {
            this._explosionAnimation.visible = false;
            this._explosionAnimation.scale.set(1, 1);
        });

        this._carExhaustAnimation = new TimelineAnimatedSprite(Loner.assetLoader.getTextures(LonerGameAssets.TTB_EXHAUST_ANIMATION), 1, 3);
        this._carExhaustAnimation.anchor.set(0.5, 0.5);
        this._carExhaustAnimation.position = LonerCar.CAR_EXHAUST_POSITION;
        this._carExhaustAnimation.visible = false;
        this.addChild(this._carExhaustAnimation);

        this._carExhaustAnimationTL = gsap.timeline({paused: true});
        this._carExhaustAnimationTL.add(() => {
            this._carExhaustAnimation.visible = true;
        });
        this._carExhaustAnimationTL.add(() => {
            Loner.soundPlayer.playSound(LonerSound.TTB_CAR_START)
        });
        this._carExhaustAnimationTL.add(this._carExhaustAnimation.getPlayTL());
        this._carExhaustAnimationTL.add(() => {
            this._carExhaustAnimation.visible = false;
        });
    }

    public getRandomBreakdownTL(): gsap.core.Timeline {
        this._breakDown01TL = gsap.timeline();
        this._breakDown01TL.add(() => {
            Loner.soundPlayer.playSound(LonerSound.TTB_CAR_ON_FIRE);
            this._engineFireAnimation.visible = true;
        });
        this._breakDown01TL.add(() => {
            this._carRunningTL.pause()
        });
        return this._breakDown01TL;
    }

    public startCar(): void {
        this._carRunningTL.restart();
    }

    public reset(): void {
        this._carSprite.visible = true;
        this._engineFireAnimation.visible = false;
    }

    public getExplosionTL(): gsap.core.Timeline {
        return this._normalExplosionTL;
    }

    public getMaxWinExplosionTL(): gsap.core.Timeline {
        return this._maxWinExplosionTL;
    }

    public getCarExhaustAnimationTL(): gsap.core.Timeline {
        return this._carExhaustAnimationTL.restart();
    }
}